import React, { useState, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  FaArrowRight, FaArrowLeft, FaCog, FaSignOutAlt,
  FaEye, FaEyeSlash, FaSms, FaGift, FaFileInvoice, FaBell,
  FaBoxOpen, FaPen, FaWrench, FaCalendarAlt, FaUserPlus, FaHandshake,
  FaUsers, FaCheckCircle, FaShieldAlt, FaHome,
} from 'react-icons/fa';
import { useProContext } from '../context/ProContext';

const ProLayout = ({ children, title }) => {
  const {
    currentUser, logout,
    brand, brandLogo, brandName,
    isAA,
    month, setMonth,
    stats, statsLoading: sL,
    clients,
    isOptionActive,
  } = useProContext();
  const navigate = useNavigate();

  const [showCA, setShowCA] = useState(false);

  const convRate = useMemo(() => {
    const p = clients.filter(c => ['approved', 'rejected'].includes(c.status)).length;
    return clients.length > 0 && p > 0 ? Math.round((p / clients.length) * 100) : 0;
  }, [clients]);

  const canNext =
    month.getFullYear() < new Date().getFullYear() ||
    (month.getFullYear() === new Date().getFullYear() && month.getMonth() < new Date().getMonth());
  const monthLabel = month.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  // Sidebar navigation — only main sections
  const navItems = [
    { to: '/optician-dashboard', icon: FaHome, label: 'Tableau de bord' },
    { to: '/optician/ongoing', icon: FaHandshake, label: 'Recommandations en cours' },
    { to: '/optician/completed', icon: FaCheckCircle, label: 'Recommandations traitées' },
    { to: '/optician/sponsors', icon: FaUsers, label: 'Utilisateurs' },
    { to: '/optician/invoices', icon: FaFileInvoice, label: 'Comptabilité' },
  ];

  return (
    <div className="h-screen flex overflow-hidden" style={{ '--brand': brand, '--brand-light': brand + '15', '--brand-bg': brand + '08' }}>

      {/* LEFT SIDEBAR */}
      <div
        className="flex-shrink-0 flex flex-col text-white overflow-y-auto"
        style={{ backgroundColor: brand, width: 'var(--pro-sidebar-w)' }}
      >
        {/* Brand */}
        <div className="px-4 pt-4 pb-3 flex items-center gap-3">
          {brandLogo && (
            <img
              src={brandLogo}
              alt=""
              className="w-8 h-8 object-contain rounded-md bg-white p-0.5 flex-shrink-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <span className="font-semibold text-sm opacity-90 truncate">{brandName}</span>
        </div>

        {/* Month nav */}
        <div className="px-4 py-2 flex items-center justify-between">
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1))} className="p-1 hover:bg-white/10 rounded text-white/50 hover:text-white">
            <FaArrowLeft className="w-[0.625rem] h-[0.625rem]" />
          </button>
          <span className="text-xs font-semibold capitalize opacity-80">{monthLabel}</span>
          <button onClick={() => canNext && setMonth(new Date(month.getFullYear(), month.getMonth() + 1))} className={`p-1 rounded ${canNext ? 'hover:bg-white/10 text-white/50 hover:text-white' : 'text-white/15'}`} disabled={!canNext}>
            <FaArrowRight className="w-[0.625rem] h-[0.625rem]" />
          </button>
        </div>

        {/* Stats */}
        <div className="px-3 py-2 space-y-1.5">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2.5">
            <p className="text-[0.625rem] text-white/50">Ventes du mois</p>
            <p className="text-2xl font-bold">{sL ? '\u2014' : stats.monthlySales}</p>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-2.5 py-2">
              <p className="text-[0.5625rem] text-white/40">Total</p>
              <p className="text-base font-bold">{sL ? '\u2014' : stats.totalSales}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-2.5 py-2">
              <p className="text-[0.5625rem] text-white/40">Conversion</p>
              <p className="text-base font-bold">{sL ? '\u2014' : `${convRate}%`}</p>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2.5 cursor-pointer hover:bg-white/15 transition-colors" onClick={() => setShowCA(!showCA)}>
            <div className="flex items-center justify-between">
              <p className="text-[0.625rem] text-white/50">CA du mois</p>
              {showCA ? <FaEyeSlash className="w-2.5 h-2.5 text-white/40" /> : <FaEye className="w-2.5 h-2.5 text-white/40" />}
            </div>
            <p className={`text-lg font-bold ${showCA ? '' : 'blur-sm select-none'}`}>{sL ? '\u2014' : `${Math.ceil(stats.monthlyRevenue)} \u20AC`}</p>
          </div>
        </div>

        {/* Divider */}
        <div className="mx-4 my-2 border-t border-white/10" />

        {/* Navigation links */}
        <div className="px-3 flex-1">
          <p className="text-[0.5625rem] font-semibold text-white/30 uppercase tracking-widest mb-2 px-1">Navigation</p>
          <div className="space-y-0.5">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `group flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-lg transition-colors text-left ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'hover:bg-white/10 text-white/60 hover:text-white/90'
                  }`
                }
              >
                <item.icon className="w-3 h-3 flex-shrink-0" />
                <span className="text-[0.6875rem]">{item.label}</span>
              </NavLink>
            ))}
          </div>
        </div>

        {/* Bottom: settings + support + logout */}
        <div className="px-3 pb-3 pt-2 border-t border-white/10 space-y-0.5">
          <NavLink
            to="/optician/support"
            className={({ isActive }) =>
              `group flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-lg transition-colors text-left ${
                isActive ? 'bg-white/20 text-white' : 'hover:bg-white/10 text-white/60 hover:text-white/90'
              }`
            }
          >
            <FaBell className="w-3 h-3 flex-shrink-0" />
            <span className="text-[0.6875rem]">Support</span>
          </NavLink>
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="group flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-lg hover:bg-white/10 transition-colors text-left text-white/40 hover:text-red-400"
          >
            <FaSignOutAlt className="w-3 h-3 flex-shrink-0" />
            <span className="text-[0.6875rem]">Déconnexion</span>
          </button>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 flex flex-col bg-gray-100 overflow-hidden min-w-0">
        {/* Header bar */}
        {title && (
          <div className="px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
            <div className="mx-auto w-full max-w-screen-3xl flex items-center justify-between gap-3 min-w-0">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  onClick={() => navigate('/optician-dashboard')}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-[0.6875rem] font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0"
                  title="Retour au tableau de bord"
                >
                  <FaArrowLeft className="w-[0.625rem] h-[0.625rem]" />
                  Retour
                </button>
                <h1 className="text-lg font-bold text-gray-900 truncate">{title}</h1>
              </div>
            </div>
          </div>
        )}

        {/* Page content */}
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-screen-3xl">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProLayout;
